import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Printer, ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { devApi } from './devApi';
import { HandoverReport } from './HandoverReport';
import axios from 'axios';

/** 完了報告書・引継ぎ資料 — 書類は複製せず参照のみ（正本はドキュメントアプリ）。 */
export default function HandoverPrint() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  // 7 · 写真欄（最大6枚）。画像はここで読み込んで報告書に渡す。
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let live = true;
    const urls = [];
    devApi.gallery(id).then(async (g) => {
      const picked = (g.groups || [])
        .flatMap((x) => (x.photos || []).map((p) => ({ ...p, group: x.name })))
        .slice(0, 6);
      const withUrls = [];
      for (const p of picked) {
        try {
          const r = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/dms/documents/${p.document_id}/file`,
            { responseType: 'blob' });
          const u = URL.createObjectURL(r.data);
          urls.push(u);
          withUrls.push({ ...p, url: u });
        } catch {
          withUrls.push(p);
        }
      }
      if (live) setPhotos(withUrls);
    }).catch(() => {});
    return () => { live = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [id]);

  useEffect(() => {
    devApi.handover(id).then(setD).catch(() => toast.error('引継ぎ資料を読み込めませんでした'));
  }, [id]);

  // 印刷のとき、報告書以外（アプリの枠・サイドバー・チャット・テストモードの帯）は
  // 消す。visibility だけだと場所は取ったままで、1枚目が白紙になる。
  const markShell = useCallback(() => {
    const node = document.querySelector('.print-handover');
    if (!node) return [];
    const marked = [];
    let el = node;
    while (el && el.parentElement && el !== document.body) {
      for (const sib of Array.from(el.parentElement.children)) {
        if (sib !== el && !sib.hasAttribute('data-print-hide')) {
          sib.setAttribute('data-print-hide', '');
          marked.push(sib);
        }
      }
      el = el.parentElement;
    }
    document.body.classList.add('printing-handover');
    return marked;
  }, []);

  useEffect(() => {
    if (!d) return undefined;
    const marked = markShell();
    const again = () => markShell();            // 後から出た要素（チャットなど）も
    window.addEventListener('beforeprint', again);
    return () => {
      window.removeEventListener('beforeprint', again);
      marked.forEach((n) => n.removeAttribute('data-print-hide'));
      document.body.classList.remove('printing-handover');
    };
  }, [d, markShell]);

  if (!d) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-handover-loading">読み込み中…</div>;
  }

  return (
    <div className="print-handover mx-auto max-w-4xl bg-white p-6 print:p-0" data-testid="dev-handover">
      <div className="flex items-center justify-between print:hidden">
        <Button variant="outline" size="sm" onClick={() => window.close()}
          data-testid="dev-handover-close">
          <ArrowLeft size={14} className="mr-1" /> 閉じる
        </Button>
        <Button size="sm" onClick={() => window.print()} data-testid="dev-handover-print-btn">
          <Printer size={14} className="mr-1" /> 印刷 / PDF保存
        </Button>
      </div>
      <HandoverReport d={d} photos={photos} />
    </div>
  );
}
